import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { obtenerMedidasPaginaContrato } from '../utils/contratoPaginaA4';
import { calcularSaltosPaginaContrato, alturaContenidoPagina } from '../utils/contratoPaginacionPreview';

/** Marca de agua centrada en una hoja A4 (una instancia por página). */
function MarcaDeAguaPagina({ logoSrc, opacidad = 0.1, anchoPorcentaje = 58 }) {
  if (!logoSrc) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <img
        src={logoSrc}
        alt=""
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${anchoPorcentaje}%`,
          maxHeight: '72%',
          objectFit: 'contain',
          opacity: opacidad,
        }}
      />
    </div>
  );
}

/**
 * Vista previa del contrato en hojas A4.
 * Paginación y tipografía siempre a tamaño A4 real; si la columna es más angosta
 * se escala con transform (evita 20–30 páginas falsas con textos largos).
 */
const ContratoVistaPreviaPaginada = ({ htmlContenido, logoSrc, fontFamily }) => {
  const medidas = useMemo(() => obtenerMedidasPaginaContrato(), []);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [saltosPagina, setSaltosPagina] = useState([0]);
  const [alturaTotal, setAlturaTotal] = useState(0);
  const [escala, setEscala] = useState(1);

  const numPaginas = saltosPagina.length;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const actualizarEscala = () => {
      const disponible = container.clientWidth || medidas.anchoPx;
      const next = Math.min(1, Math.max(disponible / medidas.anchoPx, 0.35));
      setEscala(next);
    };

    actualizarEscala();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(actualizarEscala);
      observer.observe(container);
      return () => observer.disconnect();
    }

    return undefined;
  }, [medidas.anchoPx]);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return undefined;

    const actualizar = () => {
      const total = el.scrollHeight || el.offsetHeight || 0;
      const saltos = calcularSaltosPaginaContrato(el, medidas.altoContenidoPx);
      setAlturaTotal(total);
      setSaltosPagina(saltos.length > 0 ? saltos : [0]);
    };

    // Esperar un frame para que tipografías / strong terminen de layout
    const raf = requestAnimationFrame(actualizar);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(actualizar);
      });
      observer.observe(el);
      return () => {
        cancelAnimationFrame(raf);
        observer.disconnect();
      };
    }

    return () => cancelAnimationFrame(raf);
  }, [htmlContenido, medidas.altoContenidoPx, medidas.anchoContenidoPx]);

  const estiloPaginaReal = {
    width: `${medidas.anchoPx}px`,
    height: `${medidas.altoPx}px`,
    padding: `${medidas.paddingTopPx}px ${medidas.paddingRightPx}px ${medidas.paddingBottomPx}px ${medidas.paddingLeftPx}px`,
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
    border: '1px solid #e5e7eb',
    fontFamily,
  };

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2 w-full min-w-0 max-w-full">
      {/* Medición siempre a ancho A4 real (misma tipografía que el PDF) */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-12000px',
          top: 0,
          width: `${medidas.anchoContenidoPx}px`,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
          fontFamily,
        }}
      >
        <div ref={measureRef} dangerouslySetInnerHTML={{ __html: htmlContenido }} />
      </div>

      {saltosPagina.map((offsetY, indice) => {
        const altoVisible = alturaContenidoPagina(
          saltosPagina,
          indice,
          medidas.altoContenidoPx,
          alturaTotal
        );

        return (
          <div
            key={`pagina-${indice}-${offsetY}`}
            className="flex flex-col items-center gap-1 w-full min-w-0 max-w-full"
          >
            {/* Contenedor con el tamaño visual escalado (ocupa el layout) */}
            <div
              style={{
                width: `${medidas.anchoPx * escala}px`,
                height: `${medidas.altoPx * escala}px`,
                maxWidth: '100%',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <div
                className="contrato-pagina-a4-preview"
                style={{
                  ...estiloPaginaReal,
                  transform: `scale(${escala})`,
                  transformOrigin: 'top left',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    height: `${altoVisible}px`,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      marginTop: `${-offsetY}px`,
                      position: 'relative',
                      width: '100%',
                    }}
                    dangerouslySetInnerHTML={{ __html: htmlContenido }}
                  />
                </div>
                <MarcaDeAguaPagina logoSrc={logoSrc} />
              </div>
            </div>
            <span className="text-[9px] text-gray-400" style={{ fontFamily }}>
              Pág. {indice + 1} / {numPaginas}
            </span>
          </div>
        );
      })}

      <p className="text-[10px] text-gray-400 text-center px-2 mt-2">
        Vista previa aproximada en hojas A4. Al exportar el PDF pueden variar ligeramente los cortes.
      </p>
    </div>
  );
};

export default ContratoVistaPreviaPaginada;
